import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SelectEmailComponent } from "@/Common/Components/Contacts/EmailSelectionControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { DateTime } from "@/Common/Helpers/DateTime";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Patient } from "@/Common/Models/Patient/Patient";
import { ConferenceRoom } from "@/Common/Models/Telemed/ConferenceRoom";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface TelemedInvitationProps_OptimizedV2 {
    patientInfo: Patient;
    onCloseCallback: () => void;
    onRoomUrlClickCallback: (roomUrl: string) => void;
    showAsPopup?: boolean;
}

class TelemedInvitationState_OptimizedV2 {
    public isLoadingExistingInfo: boolean;
    public isSendingInvitation: boolean; // used when user clicks send

    public selectedEmailAddress: string;
    public emailContactTypeLookupId: string;
    public errorMessage: string;
    public upcomingRooms: ConferenceRoom[]; // room 15+ minutes in the future
    public openRooms: ConferenceRoom[]; // room is open 15 min before and 1 hr after room's StartDate
    public selectedRoom: ConferenceRoom;
    public upcomingAppointments: UpcomingAppointment[];
    public shouldAddEmailAddressToPatientContact: boolean;

    public static getDefault(): TelemedInvitationState_OptimizedV2 {
        let s = new TelemedInvitationState_OptimizedV2();
        s.isLoadingExistingInfo = false;
        s.isSendingInvitation = false;
        s.selectedEmailAddress = "";
        s.emailContactTypeLookupId = "";
        s.shouldAddEmailAddressToPatientContact = false;
        return s;
    }
}

class TelemedInvitationResponse_OptimizedV2 {
    public Errors: ErrorMessage[];
    public Room: ConferenceRoom;
}

class TelemedInvitationInformation_OptimizedV2 {
    public Errors: ErrorMessage[];
    public ExistingRooms: ConferenceRoom[];
    public UpcomingAppointments: UpcomingAppointment[];
}

class TelemedInvitationComponent_OptimizedV2 extends BaseReactComponent<TelemedInvitationProps_OptimizedV2, TelemedInvitationState_OptimizedV2> {
    public constructor(props: TelemedInvitationProps_OptimizedV2) {
        super(props);
        this.state = TelemedInvitationState_OptimizedV2.getDefault();
    }

    public componentDidMount(): void {
        super.setOverlay("TelemedInvitationComponent", {
            transparent: true,
            onClicked: () => this.props.onCloseCallback()
        });

        document.addEventListener("keyup", e => this.closeOnEscape(e));

        this.getExistingRoomInformation();
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        document.removeEventListener("keyup", e => this.closeOnEscape(e));
    }

    public render(): JSX.Element {
        let showAsPopupCss = this.props.showAsPopup ? "portal-register-modal" : "";

        return (
            <div id="telemedInvitationComponent">
                <div className={`${showAsPopupCss} account-invitation`}>
                    <h3 className="header thin-font">{cacheStore.GetString("TelemedLabel")}</h3>
                    <div>

                        {this.hasError() && (
                            <div style={{ padding: "10px" }}>
                                <span className="red">{this.state.errorMessage}</span>
                            </div>
                        )}

                        {this.isWaitingForServerResponse && (
                            <div style={{ padding: "10px" }}>
                                <InformationLoadingComponent
                                    smallVersion={true}
                                    customMessage={cacheStore.GetString("WaitForTelemedServiceLabel")}
                                />
                            </div>
                        )}

                        {!this.isWaitingForServerResponse &&
                            !this.hasError() &&
                            this.renderDisclaimerText()
                        }

                        {!this.isWaitingForServerResponse && (this.hasOpenRoom || this.hasUpcomingRoom) &&
                            this.renderRoomInformation()
                        }
                        <hr />

                        {this.renderEmailControls()}

                        {this.renderActionButtons()}

                    </div>
                </div>
            </div>
        );
    }

    private renderActionButtons(): JSX.Element {
        return (
            <div className="footer clearfix">
                <div className="pull-right">
                    <button
                        key="btn-cancel"
                        className="btn btn-default push-right-5"
                        onClick={() => this.onCloseClick()}
                    >

                        <span>{cacheStore.GetString("CloseLabel")}</span>
                    </button>

                    <button
                        key="btn-refuse"
                        disabled={!this.isValid()}
                        className="btn btn-primary push-right-5"
                        onClick={() => this.onSendInvitationClick()}
                    >
                        <span>{cacheStore.GetString(this.hasOpenRoom ? "TelemedResendInvitationLabel" : "SendLabel")}</span>
                    </button>
                </div>
            </div>
        );
    }

    private renderEmailControls(): JSX.Element {
        return (
            <div>
                {!this.hasOpenRoom && (
                    <div style={{ paddingLeft: "10px", fontWeight: "bold" }}>
                        {cacheStore.GetString("CreateImmediateTeleconsultationSessionLabel")}
                    </div>
                )}

                <SelectEmailComponent
                    patientInfo={this.props.patientInfo}
                    onCloseDelegate={() => this.onCloseClick()}
                    onSelectionCompletedDelegate={(emailAddress, contactTypeLookupId) => this.onEmailAddressChange(emailAddress, contactTypeLookupId)}
                    onShouldAddEmailToPatientContactChangeDelegate={value => this.onAddEmailAddressToPatientContactChange(value)}
                    onEnterKeyDelegate={() => this.onSendInvitationClick()}
                />
            </div>
        );
    }

    private renderRoomInformation(): JSX.Element {
        const styleGroup: React.CSSProperties = { paddingTop: "15px" };
        const styleLabel: React.CSSProperties = { textAlign: "right", display: "inline-block" };
        const styleValue: React.CSSProperties = { paddingLeft: "10px", display: "inline-block" };
        const hasMultipleOpenRooms = this.state.openRooms?.length > 1;

        function formatAuthCode(code: string): string {
            return `${code.substr(0, 3)} ${code.substr(3)}`;
        }

        return (
            <div id="conferenceRoomInfo">
                <div style={{ paddingLeft: "30px" }}>
                    <div style={styleGroup}>
                        <div style={styleLabel}>{cacheStore.GetString(hasMultipleOpenRooms ? "SelectAppointmentLabel" : "SubjectLabel")}</div>

                        <div style={styleValue}>
                            {hasMultipleOpenRooms && (
                                <Combobox
                                    id="selectTimeslotTelemed"
                                    style={{ width: "auto", maxWidth: "475px" }}
                                    onChange={(selectedCode: string) => this.onSelectedRoomChanged(selectedCode)}
                                    items={this.getOpenRoomsAsDropDownItems()}
                                    selectedCode={this.state.selectedRoom.RoomId}
                                />
                            )}
                            {this.state.openRooms?.length === 1 && (
                                <div style={styleValue}>
                                    {this.getAppointmentSubjectWithDateFromRoom(this.state.selectedRoom)}
                                </div>
                            )}
                            {this.state.openRooms?.length === 0 && this.hasUpcomingRoom && (
                                <div style={styleValue}>
                                    {this.getAppointmentSubjectWithDateFromRoom(this.state.upcomingRooms[0])}
                                </div>
                            )}
                        </div>
                    </div>

                    {hasMultipleOpenRooms && (
                        <div style={styleGroup}>
                            <div style={styleLabel}>{cacheStore.GetString("ClickLinkToAccessTelemedSessionLabel")}</div>
                        </div>
                    )}

                    {this.hasOpenRoom && (
                        <div>
                            <div style={styleGroup}>
                                <div style={styleLabel}>{cacheStore.GetString("TelemedLinkLabel")}</div>
                                <div style={styleValue}><a onClick={e => this.openConferenceWindow(this.state.selectedRoom, e)}>{this.state.selectedRoom.RoomUrl}</a></div>
                            </div>
                            <div style={styleGroup}>
                                <div style={styleLabel}>{cacheStore.GetString("TelemedActivationCodeLabel")}</div>
                                <div style={styleValue}> <b>{formatAuthCode(this.state.selectedRoom.AuthCode)}</b></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderDisclaimerText(): JSX.Element {
        let vocabResourceKey = "NoUpcomingTeleconsultationAppointmentsLabel";

        if (this.hasOpenRoom)
            vocabResourceKey = this.state.openRooms.length > 1 ? "TelemedExistingRoomsDisclaimer" : "TelemedExistingRoomDisclaimer";
        else if (this.hasUpcomingRoom)
            vocabResourceKey = "TheFollowingTeleconsultationIsPlannedForThisPatient";

        return (
            <div className="conferenceRoomDisclaimer" style={{ padding: "10px" }}>
                {JSXHelper.BreakStringIntoParagraphs(cacheStore.GetString(vocabResourceKey))}
            </div>
        );
    }

    private onCloseClick(): void {
        if (typeof this.props.onCloseCallback === "function") {
            this.props.onCloseCallback();
        }
    }

    private onEmailAddressChange(emailAddress: string, contactTypeLookupId: string): void {
        this.mutateState(s => {
            s.emailContactTypeLookupId = contactTypeLookupId;
            s.selectedEmailAddress = emailAddress;
        });
    }

    private onAddEmailAddressToPatientContactChange(value: boolean): void {
        this.mutateState(s => {
            s.shouldAddEmailAddressToPatientContact = value;
        });
    }

    private isValid(): boolean {
        return this.state.selectedEmailAddress?.length > 0 &&
            !this.isWaitingForServerResponse &&
            !this.hasError();
    }

    private getOpenRoomsAsDropDownItems(): Immutable.List<ComboBoxDropDownItem> {
        return (this.state.openRooms || [])
            .map(
                existingRoom => new ComboBoxDropDownItem(
                    existingRoom.RoomId,
                    this.getAppointmentSubjectWithDateFromRoom(existingRoom),
                    null,
                    null,
                    existingRoom
                )
            )
            .toImmutable();
    }

    private getAppointmentFromRoom(
        room: ConferenceRoom,
        upcomingAppointments: UpcomingAppointment[]
    ): UpcomingAppointment {
        return upcomingAppointments?.firstOrDefault(upcomingAppointment => upcomingAppointment.AppointmentId == room.AppointmentId);
    }

    private getAppointmentSubjectWithDateFromRoom(room: ConferenceRoom): string {
        const appointment = this.getAppointmentFromRoom(room, this.state.upcomingAppointments);
        const roomStartDate = moment(room.StartDate.toString()).format("YYYY-MM-DD HH:mm");
        return appointment != null
            ? `${appointment.Subject} (${roomStartDate})`
            : cacheStore.GetString("ImmediateTeleconsultationLabel");
    }

    private onSelectedRoomChanged(selectedCode: string): void {
        this.mutateState(s => s.selectedRoom = s.openRooms.firstOrDefault(room => room.RoomId == selectedCode));
    }

    private get isWaitingForServerResponse(): boolean {
        return this.state.isSendingInvitation || this.state.isLoadingExistingInfo;
    }

    private getOrderedOpenRooms(conferenceRooms: ConferenceRoom[]): ConferenceRoom[] {
        return conferenceRooms.filter((room: ConferenceRoom) =>
            this.getRoomIsOpen(room)
        )
            .sortByProperty(room => room.StartDate);
    }

    private getOrderedUpcomingRooms(conferenceRooms: ConferenceRoom[]): ConferenceRoom[] {
        return conferenceRooms
            .filter(room => !this.getRoomIsOpen(room) && moment(room.StartDate) > moment())
            .sortByProperty(room => room.StartDate);
    }

    private get hasOpenRoom(): boolean {
        return this.state.openRooms?.length > 0;
    }

    private get hasUpcomingRoom(): boolean {
        return this.state.upcomingRooms?.length > 0;
    }

    private getRoomIsOpen(room: ConferenceRoom): boolean {
        return moment.utc(room.StartDate).subtract(15, "minute") <= moment.utc();
    }

    public hasError(): boolean {
        return this.state.errorMessage != null && this.state.errorMessage.length > 0;
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.keyCode == 27) {
            this.onCloseClick();
        }
    }

    private getExistingRoomInformation(): void {
        const client = new Client();

        let requestData = {
            PatientContactId: this.props.patientInfo.IndividualResponse.Contact_id,
            Email: this.state.selectedEmailAddress
        };

        client.Post(
            apiSettings.buildApiPath(),
            ServerOperations.Server.Myle_Patient_Telemed_GetInvitationInformation,
            requestData,
            response => this.onGetExistingRoomInformation(response),
            err => this.onError(err)
        );

        this.mutateState(s => {
            s.isLoadingExistingInfo = true;
        });
    }

    private onGetExistingRoomInformation(response: TelemedInvitationInformation_OptimizedV2): void {
        if (response.Errors?.length > 0) {
            this.mutateState(s => {
                s.isLoadingExistingInfo = false;
                s.upcomingAppointments = null;
                s.errorMessage = response.Errors[0].Message;
                s.upcomingRooms = null;
                s.openRooms = null;
                s.selectedRoom = null;
            });

            return;
        }

        let openRooms = this.getOrderedOpenRooms(response.ExistingRooms);

        this.mutateState(s => {
            s.isLoadingExistingInfo = false;
            if (response.ExistingRooms?.length > 0) {
                s.openRooms = openRooms;
                s.selectedRoom = openRooms[0];
                s.upcomingRooms = this.getOrderedUpcomingRooms(response.ExistingRooms);
            }
            else {
                s.openRooms = null;
                s.upcomingRooms = null;
                s.selectedRoom = null;
            }

            if (response.UpcomingAppointments != null && response.UpcomingAppointments.length > 0)
                s.upcomingAppointments = response.UpcomingAppointments;
            else
                s.upcomingAppointments = null;
        });
    }

    private getSelectedUpcomingAppointmentDateTime(): DateTime {
        return this.state.selectedRoom?.AppointmentId == null ?
            new Date() :
            this.state.upcomingAppointments.firstOrDefault(appointment => appointment.AppointmentId == this.state.selectedRoom.AppointmentId).DateTime;
    }

    private onSendInvitationClick(): void {
        const client = new Client();
        const startTime = this.getSelectedUpcomingAppointmentDateTime();

        let requestData = {
            AppointmentId: this.state.selectedRoom?.AppointmentId,
            PatientContactId: this.props.patientInfo.IndividualResponse.Contact_id,
            Email: this.state.selectedEmailAddress,
            ShouldAddEmailAddressToPatientContact: this.state.shouldAddEmailAddressToPatientContact,
            PatientFirstName: this.props.patientInfo.IndividualResponse.FirstName,
            PatientPreferredName: this.props.patientInfo.IndividualResponse.PreferredName,
            PatientLastName: this.props.patientInfo.IndividualResponse.LastName,
            StartTime: moment(startTime).format(), // server expects DateTimeOffesst, moment.format() outputs correct string e.g."2017-06-16T12:00:00-04:00"
            ActiveSiteId: sessionStore.getCurrentEmployee().Model.ActiveSiteId,
            EmailContactTypeLookupId: this.state.emailContactTypeLookupId,
            ResendInvite: this.hasOpenRoom
        };

        this.mutateState(
            s => s.isSendingInvitation = true,
            () => client.Post(
                apiSettings.buildApiPath(),
                ServerOperations.Server.Myle_Patient_Telemed_SendInvitation,
                requestData,
                response => this.onSendInvitationSuccess(response),
                err => this.onError(err)
            )
        );
    }

    private onSendInvitationSuccess(response: TelemedInvitationResponse_OptimizedV2): void {
        if (this.state.shouldAddEmailAddressToPatientContact) this.reloadPatient();

        if (response.Errors != null && response.Errors.length > 0) {
            this.mutateState(s => {
                s.isSendingInvitation = false;
                s.errorMessage = response.Errors[0].Message;
            });

            return;
        }

        const isImmediate = this.state.selectedRoom?.AppointmentId == null;
        this.mutateState(
            s => s.isSendingInvitation = false,
            () => {
                if (isImmediate)
                    this.openConferenceWindow(response.Room);

                this.props.onCloseCallback();
            }
        );
    }

    private onError(error: string): void {
        if (this.state.shouldAddEmailAddressToPatientContact) this.reloadPatient();

        this.mutateState(s => {
            s.isLoadingExistingInfo = false;
            s.isSendingInvitation = false;
            s.errorMessage = cacheStore.GetString("ErrorSendingMessageToPortalLabel");
            console.log(error);
        });
    }

    private reloadPatient(): void {
        patientStore.ActionsProfile.loadPatient(this.props.patientInfo.IndividualResponse.Contact_id);
    }

    private openConferenceWindow(room: ConferenceRoom, e?: any): void {
        if (e && e.preventDefault) e.preventDefault();

        window.setTimeout(
            () => {
                const roomUrl = apiSettings.buildAppUrl(
                    "api/telemed/login",
                    {
                        auth: true,
                        params: {
                            redirectUrl: room.RoomUrl
                        }
                    }
                );

                if (settingsStore.configuration.get("IsTelemedWindowInMyleWindow") && cacheStore.ClinicSettings.EnableTelemedPictureInPicture) {
                    this.props.onRoomUrlClickCallback(roomUrl);
                }
                else {
                    const newWindow = window.open(roomUrl, "_medfarTelemedConference", "height = 480, width = 600,modal=yes,alwaysRaised=yes");
                    if (newWindow.focus) {
                        newWindow.focus();
                    }
                }
            },
            50
        );
    }
}

export { TelemedInvitationComponent_OptimizedV2 };
